/**
 * Merge multiple Playwright `trace.zip` archives into a single archive.
 *
 * A Playwright trace.zip is natively a MULTI-`.trace` container: each browser
 * context contributes a `<stem>.trace` (+ paired `<stem>.network` / optional
 * `<stem>.stacks`, all newline-delimited JSON) plus content-addressed
 * `resources/<sha1>` bodies. The trace viewer renders multiple `.trace` entries
 * as one session, and our own parser (`parseTraceActions`) globs every `*.trace`
 * entry — so merging is a pure zip-union with no event re-writing:
 *
 *   - `*.trace` / `*.network` / `*.stacks` are copied under a per-source prefix
 *     (`s0-…`, `s1-…`). The prefix is applied to ALL three so a context's
 *     stem-pairing survives, and it guarantees no collision across sources that
 *     happen to use the same internal stem.
 *   - `resources/<sha1>` entries keep their original names (the `.trace` events
 *     reference them by sha1) and are deduped by name — content-addressed, so an
 *     identical name means identical bytes.
 *   - Any other entries (legends, placeholders) are dropped; they aren't part of
 *     the navigable trace and would collide.
 *
 * Used for both intra-segment chunks (re-auth context swaps within one session)
 * and inter-segment stitching (one trace per wait-node segment).
 */

import AdmZip from 'adm-zip';

const TRACE_ENTRY_SUFFIXES = ['.trace', '.network', '.stacks'] as const;

export interface MergeTraceResult {
  path: string;
  /** Number of source archives that contributed at least one entry. */
  sourceCount: number;
  /** Sources that were unreadable or contributed nothing (input count − sourceCount). */
  skippedCount: number;
}

/**
 * Union `zipPaths` (in order) into a single trace.zip written to `outPath`.
 * Unreadable/missing sources are skipped (best-effort); the merge still
 * succeeds with whatever sources were readable.
 */
export function mergeTraceArchives(zipPaths: string[], outPath: string): MergeTraceResult {
  const out = new AdmZip();
  const seenResources = new Set<string>();
  let sourceCount = 0;

  zipPaths.forEach((zipPath, sourceIndex) => {
    let zip: AdmZip;
    try {
      zip = new AdmZip(zipPath);
    } catch {
      return; // skip unreadable / missing source
    }

    let contributed = false;
    for (const entry of zip.getEntries()) {
      if (entry.isDirectory) continue;
      const name = entry.entryName;

      if (name.startsWith('resources/')) {
        if (seenResources.has(name)) continue; // content-addressed dedup
        seenResources.add(name);
        out.addFile(name, entry.getData());
        contributed = true;
        continue;
      }

      if (TRACE_ENTRY_SUFFIXES.some((suffix) => name.endsWith(suffix))) {
        out.addFile(`s${sourceIndex}-${name}`, entry.getData());
        contributed = true;
      }
      // Everything else (trace-legend.txt, README placeholders, …) is dropped.
    }

    if (contributed) sourceCount += 1;
  });

  out.writeZip(outPath);
  return { path: outPath, sourceCount, skippedCount: zipPaths.length - sourceCount };
}
